/**
 * Application integrations: the vocabulary and pure checks both sides share (ADR 0033).
 *
 * A company connects an outside application once (Settings -> Integrations -> Applications) and
 * PeopleOrbit then creates each employee there and, if the company chose "Add and update", keeps
 * their details current. ONE-WAY and ADD-ONLY BY DESIGN: there is no deactivate or delete — leaving,
 * notice period or leave never touch the application (owner decision 2026-10-10).
 *
 * What lives here, and why here: the contract's Zod schemas, the API's refusals and the web form's
 * inline errors must agree on what a valid address, header and template are, so the rules are
 * written once. Everything is pure and RN-safe (no Node built-ins; the URL check is a parser, not
 * `new URL`, which React Native implements only partly). The network side — DNS vetting, the
 * pinned connection — is server-only and lives in `@hrms/integrations`.
 */

// ---------------------------------------------------------------------------
// Fixed vocabularies
// ---------------------------------------------------------------------------

/** Simple webhook: we POST our standard payload. Custom: HR writes the request per operation. */
export const APP_INTEGRATION_KINDS = ['simple_webhook', 'custom'] as const;
export type AppIntegrationKind = (typeof APP_INTEGRATION_KINDS)[number];

/**
 * `breaker_paused` is set by the circuit breaker only; HR resumes it by going live again.
 * A new application starts in `dry_run`.
 */
export const APP_INTEGRATION_MODES = ['live', 'dry_run', 'paused', 'breaker_paused'] as const;
export type AppIntegrationMode = (typeof APP_INTEGRATION_MODES)[number];

export const APP_INTEGRATION_AUTH_TYPES = [
  'none',
  'bearer',
  'basic',
  'api_key',
  'oauth2_client_credentials',
] as const;
export type AppIntegrationAuthType = (typeof APP_INTEGRATION_AUTH_TYPES)[number];

/** There is no delete or deactivate operation, on purpose. */
export const APP_INTEGRATION_OPERATION_KINDS = ['create', 'update', 'lookup'] as const;
export type AppIntegrationOperationKind = (typeof APP_INTEGRATION_OPERATION_KINDS)[number];

export const APP_INTEGRATION_WRITE_METHODS = ['POST', 'PUT', 'PATCH'] as const;
export const APP_INTEGRATION_LOOKUP_METHODS = ['GET', 'POST'] as const;

/** When a new employee is sent. `on_joining_date` is the default (06:00 location time that day). */
export const APP_INTEGRATION_SEND_TIMINGS = ['on_joining_date', 'on_create'] as const;
export type AppIntegrationSendTiming = (typeof APP_INTEGRATION_SEND_TIMINGS)[number];

/** Types a question HR answers per employee can have (same set as employee custom fields). */
export const APP_INTEGRATION_INPUT_TYPES = ['text', 'number', 'date', 'dropdown'] as const;
export type AppIntegrationInputType = (typeof APP_INTEGRATION_INPUT_TYPES)[number];

/** Whether the person exists in the application, as far as we know. */
export const APP_INTEGRATION_PRESENCE = ['unknown', 'present', 'absent', 'ambiguous'] as const;
export type AppIntegrationPresence = (typeof APP_INTEGRATION_PRESENCE)[number];

/**
 * One employee's status in one application, as the list chip and profile card show it. Derived by
 * the API from the state row and the latest job — never stored as such.
 */
export const APP_INTEGRATION_SYNC_STATUSES = [
  'not_synced',
  'scheduled',
  'grace',
  'waiting',
  'dry_run',
  'pending',
  'synced',
  'skipped',
  'needs_input',
  'ambiguous',
  'outside_audience',
  'out_of_date',
  'failed',
] as const;
export type AppIntegrationSyncStatus = (typeof APP_INTEGRATION_SYNC_STATUSES)[number];

export const APP_INTEGRATION_JOB_STATUSES = [
  'scheduled',
  'grace',
  'pending',
  'running',
  'synced',
  'skipped',
  'failed',
  'cancelled',
  'dry_run',
  'waiting',
  'ambiguous',
] as const;
export type AppIntegrationJobStatus = (typeof APP_INTEGRATION_JOB_STATUSES)[number];

export const APP_INTEGRATION_JOB_TRIGGERS = [
  'create',
  'joined',
  'update',
  'sweep',
  'manual',
  'backfill',
  'resync',
] as const;
export type AppIntegrationJobTrigger = (typeof APP_INTEGRATION_JOB_TRIGGERS)[number];

export const APP_INTEGRATION_SKIP_REASONS = [
  'already_exists',
  'already_synced',
  'exited',
  'archived',
  'outside_audience',
  'unticked',
  'no_remote_key',
  'unchanged',
] as const;
export type AppIntegrationSkipReason = (typeof APP_INTEGRATION_SKIP_REASONS)[number];

export const APP_INTEGRATION_BATCH_KINDS = ['backfill', 'resync', 'send_waiting'] as const;
export type AppIntegrationBatchKind = (typeof APP_INTEGRATION_BATCH_KINDS)[number];

export const APP_INTEGRATION_BATCH_STATUSES = [
  'counting',
  'ready',
  'running',
  'paused',
  'stopped',
  'completed',
] as const;
export type AppIntegrationBatchStatus = (typeof APP_INTEGRATION_BATCH_STATUSES)[number];

// ---------------------------------------------------------------------------
// Limits
// ---------------------------------------------------------------------------

export const APP_INTEGRATION_RATE_MIN = 1;
export const APP_INTEGRATION_RATE_MAX = 20;
export const APP_INTEGRATION_RATE_DEFAULT = 5;
/** Characters of a body/query template once serialised. */
export const APP_INTEGRATION_TEMPLATE_MAX_CHARS = 16_000;
export const APP_INTEGRATION_MAX_HEADERS = 20;
export const APP_INTEGRATION_MAX_INPUTS = 20;
/** Minutes an immediate create waits so a mistake can be archived or corrected first. */
export const APP_INTEGRATION_GRACE_MINUTES = 10;
/** Local time a joining-date create goes out. */
export const APP_INTEGRATION_JOINING_SEND_HOUR = 6;

// ---------------------------------------------------------------------------
// Addresses
// ---------------------------------------------------------------------------

/**
 * Ports of services that are not web APIs (remote login, mail, databases, caches). Any other port
 * is allowed on HTTPS (decision G9): a company's own tool often runs on a non-standard one.
 */
export const APP_INTEGRATION_BLOCKED_PORTS: readonly number[] = [
  22, 23, 25, 110, 143, 465, 587, 993, 995, 1433, 1521, 3306, 5432, 6379, 9200, 11211, 27017,
];

/** Query parameter names that carry a credential; such a value belongs in a secret header. */
const SECRET_QUERY_PARAM =
  /^(api[_-]?key|apikey|key|token|access[_-]?token|secret|client[_-]?secret|password|pwd|auth)$/i;

const URL_PATTERN =
  /^https:\/\/(?:([^@/?#]*)@)?(\[[0-9a-fA-F:.]+\]|[^:/?#[\]@]+)(?::(\d{1,5}))?(\/[^?#]*)?(?:\?([^#]*))?(?:#.*)?$/;

/**
 * Why an address HR typed cannot be used, or `null` when it can. Covers what can be decided from
 * the text alone; the server additionally resolves the host and refuses private and internal
 * addresses (that needs DNS, so it is not here).
 */
export function appIntegrationUrlProblem(raw: string): string | null {
  const value = raw.trim();
  if (value.length === 0) return 'Enter an address.';
  if (value.length > 2000) return 'The address is too long.';
  if (/^http:\/\//i.test(value)) return 'Use an https:// address. Plain http is not allowed.';
  const match = URL_PATTERN.exec(value);
  if (!match) return 'Enter a full address starting with https://';
  const [, userInfo, host, port, , query] = match;
  if (userInfo !== undefined) {
    return 'Remove the user name or password from the address and use the Login section instead.';
  }
  if (!host || host.length > 253) return 'Enter a valid host name.';
  if (port !== undefined) {
    const n = Number(port);
    if (n < 1 || n > 65_535) return 'The port must be between 1 and 65535.';
    if (APP_INTEGRATION_BLOCKED_PORTS.includes(n)) {
      return `Port ${n} is used by a non-web service and is not allowed.`;
    }
  }
  if (query) {
    for (const pair of query.split('&')) {
      const name = decodeURIComponentSafe(pair.split('=')[0] ?? '');
      if (SECRET_QUERY_PARAM.test(name)) {
        return `Do not put "${name}" in the address; add it as a secret header instead.`;
      }
    }
  }
  return null;
}

function decodeURIComponentSafe(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/**
 * A path HR types for an operation ("/users", "/users/{{externalId}}"), appended to the base
 * address. It may not change the host, carry credentials, or contain a full URL.
 */
export function appIntegrationPathProblem(raw: string): string | null {
  const value = raw.trim();
  if (value.length === 0) return null; // the base address itself
  if (value.length > 1000) return 'The path is too long.';
  if (!value.startsWith('/')) return 'The path must start with /';
  if (value.startsWith('//') || /^\/[a-z]+:/i.test(value) || value.includes('://')) {
    return 'The path must not contain another address.';
  }
  // Spaces inside `{{ … }}` are fine; anywhere else they are a typo.
  if (/[\s\\]/.test(value.replace(PLACEHOLDER, ''))) {
    return 'The path must not contain spaces or backslashes.';
  }
  const queryAt = value.indexOf('?');
  if (queryAt >= 0) {
    for (const pair of value.slice(queryAt + 1).split('&')) {
      const name = decodeURIComponentSafe(pair.split('=')[0] ?? '');
      if (SECRET_QUERY_PARAM.test(name)) {
        return `Do not put "${name}" in the path; add it as a secret header instead.`;
      }
    }
  }
  return null;
}

/** Header names that almost always carry a credential: the form turns "secret" on for them. */
export function isLikelySecretHeader(name: string): boolean {
  return (
    /^(authorization|proxy-authorization|cookie)$/i.test(name) ||
    /(key|token|secret|password|signature|auth)/i.test(name)
  );
}

/** Headers PeopleOrbit sets itself; HR may not override them. */
export const APP_INTEGRATION_RESERVED_HEADERS: readonly string[] = [
  'host',
  'content-length',
  'content-type',
  'transfer-encoding',
  'connection',
  'idempotency-key',
  'x-peopleorbit-event',
  'x-peopleorbit-signature',
  'x-peopleorbit-timestamp',
];

const HEADER_NAME = /^[A-Za-z0-9!#$%&'*+.^_`|~-]{1,100}$/;

export function appIntegrationHeaderProblem(name: string): string | null {
  if (!HEADER_NAME.test(name)) return 'Use letters, numbers and dashes for a header name.';
  if (APP_INTEGRATION_RESERVED_HEADERS.includes(name.toLowerCase())) {
    return `${name} is set by PeopleOrbit and cannot be changed.`;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Templates and placeholders
// ---------------------------------------------------------------------------

/**
 * Employee facts a template may use, as `{{employee.<name>}}`. A WHITELIST: anything not here is
 * refused. Never available — and never to be added — date of birth, Aadhaar, PAN, national id,
 * bank details, salary or any compensation, or any other encrypted personal data (invariant 7).
 * `status` is only ever `pre_joining` or `active`: notice period, leave and exit are sensitive HR
 * facts that do not leave PeopleOrbit (N2).
 */
export const APP_INTEGRATION_EMPLOYEE_FIELDS = [
  'id',
  'employeeCode',
  'firstName',
  'lastName',
  'fullName',
  'workEmail',
  'personalEmail',
  'phone',
  'workPhone',
  'joiningDate',
  'status',
  'department',
  'designation',
  'location',
  'legalEntity',
  'managerName',
  'managerEmail',
  'managerEmployeeCode',
] as const;
export type AppIntegrationEmployeeField = (typeof APP_INTEGRATION_EMPLOYEE_FIELDS)[number];

/** Fields that identify the person in the application; usable to address an update (D7). */
export const APP_INTEGRATION_KEY_FIELDS: readonly AppIntegrationEmployeeField[] = [
  'workEmail',
  'employeeCode',
  'id',
];

export const APP_INTEGRATION_FILTERS = [
  'default',
  'upper',
  'lower',
  'date',
  'omitIfEmpty',
] as const;
export type AppIntegrationFilter = (typeof APP_INTEGRATION_FILTERS)[number];

/** `{{ name }}` or `{{ name | filter | filter("arg") }}`. Group 1: the name; group 2: the filters. */
export const APP_INTEGRATION_PLACEHOLDER_SOURCE = String.raw`\{\{\s*([^{}|]+?)\s*((?:\|[^{}]*)?)\}\}`;
const PLACEHOLDER = new RegExp(APP_INTEGRATION_PLACEHOLDER_SOURCE, 'g');
const FILTER_CALL = /^\s*([a-zA-Z]+)\s*(?:\(\s*"([^"]*)"\s*\))?\s*$/;
const KEY = /^[a-z][a-z0-9_]{0,59}$/;

export interface ParsedPlaceholder {
  /** Exactly as written between the braces, trimmed: `employee.workEmail`. */
  readonly name: string;
  readonly source: 'employee' | 'custom' | 'input' | 'externalId';
  /** The field / custom-field key / question key; `externalId` for the remote id. */
  readonly key: string;
  readonly filters: readonly { readonly name: string; readonly arg: string | null }[];
}

export type PlaceholderLocation = 'path' | 'query' | 'body';

export interface TemplateProblem {
  readonly placeholder: string;
  readonly message: string;
}

/** Splits a placeholder's text into source, key and filters, or explains why it is not valid. */
export function parsePlaceholder(
  name: string,
  filterText: string,
): ParsedPlaceholder | { readonly problem: string } {
  const filters: { name: string; arg: string | null }[] = [];
  for (const part of filterText.split('|').slice(1)) {
    const call = FILTER_CALL.exec(part);
    if (!call || !(APP_INTEGRATION_FILTERS as readonly string[]).includes(call[1]!)) {
      return { problem: `Unknown filter "${part.trim()}"` };
    }
    filters.push({ name: call[1]!, arg: call[2] ?? null });
  }
  if (name === 'externalId') return { name, source: 'externalId', key: 'externalId', filters };
  const [head, second, third, ...rest] = name.split('.');
  if (rest.length > 0) return { problem: `Unknown placeholder "${name}"` };
  if (head === 'employee' && second === 'custom' && third && KEY.test(third)) {
    return { name, source: 'custom', key: third, filters };
  }
  if (head === 'employee' && second && third === undefined) {
    if ((APP_INTEGRATION_EMPLOYEE_FIELDS as readonly string[]).includes(second)) {
      return { name, source: 'employee', key: second, filters };
    }
    return { problem: `"${name}" is not available to applications` };
  }
  if (head === 'input' && second && third === undefined && KEY.test(second)) {
    return { name, source: 'input', key: second, filters };
  }
  return { problem: `Unknown placeholder "${name}"` };
}

/** Every `{{…}}` inside a string. */
export function placeholdersIn(text: string): { name: string; filters: string; raw: string }[] {
  const found: { name: string; filters: string; raw: string }[] = [];
  for (const m of text.matchAll(PLACEHOLDER)) {
    found.push({ name: m[1]!.trim(), filters: m[2] ?? '', raw: m[0] });
  }
  return found;
}

/** Every string inside a JSON value, depth first (keys included: a key may not be a placeholder). */
function* stringsIn(value: unknown, depth = 0): Generator<{ text: string; isKey: boolean }> {
  if (depth > 20) return;
  if (typeof value === 'string') yield { text: value, isKey: false };
  else if (Array.isArray(value)) for (const v of value) yield* stringsIn(v, depth + 1);
  else if (value && typeof value === 'object') {
    for (const [k, v] of Object.entries(value)) {
      yield { text: k, isKey: true };
      yield* stringsIn(v, depth + 1);
    }
  }
}

/**
 * Checks a template (a path string, a query object or a JSON body) against the rules. Questions
 * (`input.*`) must be among `inputKeys`; custom fields are checked against the company's
 * "may be shared" list by the server, which knows it. `{{externalId}}` only makes sense where the
 * application's own id addresses someone: the path or query of an update.
 */
export function appIntegrationTemplateProblems(
  template: unknown,
  where: PlaceholderLocation,
  operation: AppIntegrationOperationKind,
  inputKeys: readonly string[],
): TemplateProblem[] {
  const problems: TemplateProblem[] = [];
  for (const { text, isKey } of stringsIn(template)) {
    for (const p of placeholdersIn(text)) {
      if (isKey) {
        problems.push({
          placeholder: p.raw,
          message: 'A placeholder cannot be used as a field name',
        });
        continue;
      }
      const parsed = parsePlaceholder(p.name, p.filters);
      if ('problem' in parsed) {
        problems.push({ placeholder: p.raw, message: parsed.problem });
        continue;
      }
      if (parsed.source === 'input' && !inputKeys.includes(parsed.key)) {
        problems.push({
          placeholder: p.raw,
          message: `There is no question with the key "${parsed.key}"`,
        });
      }
      if (parsed.source === 'externalId' && !(operation === 'update' && where !== 'body')) {
        problems.push({
          placeholder: p.raw,
          message: "{{externalId}} can only be used in an update's path or query",
        });
      }
    }
  }
  return problems;
}

/** Custom-field keys a template uses (`employee.custom.<key>`), for the server's "shared" check. */
export function customFieldKeysIn(template: unknown): string[] {
  const keys = new Set<string>();
  for (const { text } of stringsIn(template)) {
    for (const p of placeholdersIn(text)) {
      const parsed = parsePlaceholder(p.name, p.filters);
      if (!('problem' in parsed) && parsed.source === 'custom') keys.add(parsed.key);
    }
  }
  return [...keys];
}

/** Serialised size of a template, for the 16,000-character cap. */
export function appIntegrationTemplateSize(template: unknown): number {
  return JSON.stringify(template ?? null).length;
}

// ---------------------------------------------------------------------------
// Reply rules
// ---------------------------------------------------------------------------

/** A dotted path into a JSON reply: `data.id`, `result.users`. Empty = the reply itself. */
export const APP_INTEGRATION_REPLY_PATH = /^$|^[A-Za-z_$][\w$-]*(\.[A-Za-z_$0-9][\w$-]*)*$/;

/** Reads a dotted path from a parsed JSON value; `undefined` when any step is missing. */
export function readReplyPath(value: unknown, path: string): unknown {
  if (path === '') return value;
  let current: unknown = value;
  for (const step of path.split('.')) {
    if (current === null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[step];
  }
  return current;
}
